import { LedgerType, LedgerDirection } from "../../../../__generated__/graphql";
import { UserWalletLedgersQuery } from "../../../../__generated__/graphql";

type WalletLedgerItem = UserWalletLedgersQuery["userWalletLedgers"][number];

export const formatLedgerType = (ledgerType: LedgerType): string => {
  const typeMap: Record<LedgerType, string> = {
    TOPUP: "충전",
    DEPOSIT_HOLD: "챌린지 참가",
    DEPOSIT_RELEASE: "예치금 환급",
    DEPOSIT_FORFEIT: "예치금 몰수",
    DEPOSIT_CANCEL: "예치금 취소",
    PRIZE_PAYOUT: "페이백 지급",
    WITHDRAWAL_HOLD: "출금 요청",
    WITHDRAWAL_RELEASE: "출금 취소",
    WITHDRAWAL_PAYOUT: "출금 완료",
  };
  return typeMap[ledgerType] || ledgerType;
};

export const formatLedgerAmount = (
  amount: number,
  direction: LedgerDirection
): string => {
  const sign = direction === "IN" ? "+" : "-";
  return `${sign}${amount.toLocaleString()}원`;
};

export const calculateMaxWithdrawable = (availableBalance: number): number => {
  return Math.floor(availableBalance / 1000) * 1000;
};

export const formatDate = (
  date: string | Date,
  format: "item" | "group"
): string => {
  const d = typeof date === "string" ? new Date(date) : date;

  if (format === "item") {
    // MM. DD 형식 (예: "01. 08")
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${month}. ${day}`;
  } else {
    // YYYY. MM 형식 (예: "2026. 01")
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${year}. ${month}`;
  }
};

export const groupLedgersByMonth = (
  ledgers: WalletLedgerItem[]
): { [yearMonth: string]: WalletLedgerItem[] } => {
  const grouped: { [yearMonth: string]: WalletLedgerItem[] } = {};

  ledgers.forEach((ledger) => {
    const date = new Date(ledger.createdAt);
    const yearMonth = formatDate(date, "group");

    if (!grouped[yearMonth]) {
      grouped[yearMonth] = [];
    }
    grouped[yearMonth].push(ledger);
  });

  // 최신 순으로 정렬 (연도/월 내림차순)
  const sortedKeys = Object.keys(grouped).sort((a, b) => {
    const [yearA, monthA] = a.split(". ").map(Number);
    const [yearB, monthB] = b.split(". ").map(Number);

    if (yearA !== yearB) {
      return yearB - yearA;
    }
    return monthB - monthA;
  });

  const sortedGrouped: { [yearMonth: string]: WalletLedgerItem[] } = {};
  sortedKeys.forEach((key) => {
    sortedGrouped[key] = grouped[key];
  });

  return sortedGrouped;
};

/**
 * ledger 데이터를 기반으로 예치금과 페이백 금액을 계산합니다.
 * @param ledgers WalletLedger 배열
 * @returns { depositBalance: number, paybackBalance: number } 예치금과 페이백 금액
 */
export const calculateDepositAndPaybackBalance = (
  ledgers: WalletLedgerItem[]
): { depositBalance: number; paybackBalance: number } => {
  // 시간순으로 정렬 (오래된 것부터)
  const sortedLedgers = [...ledgers].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  let depositBalance = 0;
  let paybackBalance = 0;

  sortedLedgers.forEach((ledger) => {
    const amount = ledger.amount;
    const isIn = ledger.direction === "IN";
    const delta = isIn ? amount : -amount;

    // 예치금 관련 타입
    if (
      ledger.type === "TOPUP" ||
      ledger.type === "DEPOSIT_RELEASE" ||
      ledger.type === "DEPOSIT_CANCEL"
    ) {
      depositBalance += delta;
    }
    // 예치금 차감 관련 (보류, 몰수)
    else if (
      ledger.type === "DEPOSIT_HOLD" ||
      ledger.type === "DEPOSIT_FORFEIT"
    ) {
      depositBalance += delta;
    }
    // 페이백 관련 타입
    else if (ledger.type === "PRIZE_PAYOUT") {
      paybackBalance += delta;
    }
    // 출금 관련은 현재 예치금/페이백 비율로 분배
    else if (
      ledger.type === "WITHDRAWAL_HOLD" ||
      ledger.type === "WITHDRAWAL_RELEASE" ||
      ledger.type === "WITHDRAWAL_PAYOUT"
    ) {
      if (isIn) {
        // 출금 취소 등으로 다시 들어온 경우
        // 출금 시점의 비율을 유지하기 어려우므로
        // 현재 잔액 비율로 분배
        const totalBefore = depositBalance + paybackBalance;
        if (totalBefore > 0) {
          const depositRatio = depositBalance / totalBefore;
          const paybackRatio = paybackBalance / totalBefore;
          depositBalance += amount * depositRatio;
          paybackBalance += amount * paybackRatio;
        } else {
          // 잔액이 0이었던 경우 예치금으로 처리
          depositBalance += amount;
        }
      } else {
        // 출금 시 예치금과 페이백을 비율로 차감
        const totalBefore = depositBalance + paybackBalance;
        if (totalBefore > 0) {
          const depositRatio = depositBalance / totalBefore;
          const paybackRatio = paybackBalance / totalBefore;
          depositBalance -= amount * depositRatio;
          paybackBalance -= amount * paybackRatio;
        } else {
          // 잔액이 0이었던 경우 예치금에서 차감
          depositBalance -= amount;
        }
      }
    }
  });

  return {
    depositBalance: Math.max(0, Math.round(depositBalance)),
    paybackBalance: Math.max(0, Math.round(paybackBalance)),
  };
};
